import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Settings {
  theme: 'light' | 'dark';
  language: 'en' | 'sw';
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    emailNotifications: boolean;
    taskReminders: boolean;
    candidateUpdates: boolean;
    whatsappNotifications: boolean;
  };
  autoSave: boolean;
  itemsPerPage: number;
  app: {
    defaultView: string;
    autoRefresh: boolean;
    showQuickActions: boolean;
    compactMode: boolean;
    applicationName: string;
    defaultUserRole: string;
    darkMode: boolean;
  };
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  updateNotificationSettings: (notifications: Partial<Settings['notifications']>) => void;
  updateAppSettings: (app: Partial<Settings['app']>) => void;
  toggleDarkMode: () => void;
  resetSettings: () => void;
}

const defaultSettings: Settings = {
  theme: 'light',
  language: 'en',
  notifications: {
    email: true,
    push: true,
    sms: false,
    emailNotifications: true,
    taskReminders: true,
    candidateUpdates: true,
    whatsappNotifications: true
  },
  autoSave: true,
  itemsPerPage: 10,
  app: {
    defaultView: 'dashboard',
    autoRefresh: true,
    showQuickActions: true,
    compactMode: false,
    applicationName: 'Recruitment CRM',
    defaultUserRole: 'user',
    darkMode: false
  }
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    const savedSettings = localStorage.getItem('recruitment-settings');
    return savedSettings ? { ...defaultSettings, ...JSON.parse(savedSettings) } : defaultSettings;
  });

  const updateSettings = (newSettings: Partial<Settings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    localStorage.setItem('recruitment-settings', JSON.stringify(updatedSettings));
  };

  const updateNotificationSettings = (notifications: Partial<Settings['notifications']>) => {
    updateSettings({ notifications: { ...settings.notifications, ...notifications } });
  };

  const updateAppSettings = (app: Partial<Settings['app']>) => {
    updateSettings({ app: { ...settings.app, ...app } });
  };

  const toggleDarkMode = () => {
    const newMode = !settings.app.darkMode;
    updateAppSettings({ darkMode: newMode });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.setItem('recruitment-settings', JSON.stringify(defaultSettings));
  };

  const value: SettingsContextType = {
    settings,
    updateSettings,
    updateNotificationSettings,
    updateAppSettings,
    toggleDarkMode,
    resetSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};